import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../../lib/i18n';
import { queryKeys } from '../../../lib/query-client';
import { fetchConnectors } from '../../../lib/connectors';
import { connectorDisplayName, connectorForSlug } from '../../../lib/skill-connector';
import { connectorSlugName } from '../../../lib/routine-groups';
import { ConnectorLogo } from '../connectors/ConnectorLogo';
import { Card, Typography } from '../ui';
import styles from './routines.legacy.module.css';

// The desktop list's LEFT column: one row per group, each the same title card the single-column layout
// puts above its rows — logo, name, count (Figma 18352-340460).
//
// It is a navigation list AND a position report. Clicking a row scrolls the stream to that group;
// scrolling the stream moves the highlight. The highlight is the DS Card's own `selected` (Active)
// state rather than a colour of its own, which is what that prop is for.
//
// ⚠️ Desktop only. Below 720 the groups keep their title card ABOVE their own rows — a rail beside a
// one-column list would cost most of a phone's width to say what the heading already says.
export function RoutineRail({
  sections,
  activeKey,
  onPick,
}: {
  sections: { key: string; slug?: string; count: number }[];
  activeKey: string | null;
  onPick: (key: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <nav className={styles.rail} aria-label={t('routines.title')}>
      {sections.map((section) => (
        <RailRow
          key={section.key}
          slug={section.slug}
          count={section.count}
          selected={section.key === activeKey}
          onPick={() => onPick(section.key)}
        />
      ))}
    </nav>
  );
}

function RailRow({
  slug,
  count,
  selected,
  onPick,
}: {
  slug?: string;
  count: number;
  selected: boolean;
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors });
  const connector = slug ? connectorForSlug(slug, data?.connectors) : null;
  // Same fallback chain as the single-column title card: the vendor's own spelling when the connectors
  // read has landed, the slug otherwise, and the unattributed group's own name when there is no slug.
  const name = (connector ? connectorDisplayName(connector) : connectorSlugName(slug)) || t('routines.group.general');

  return (
    <Card
      component="button"
      size="sm"
      interactive
      selected={selected}
      className={styles.railRow}
      aria-current={selected ? 'true' : undefined}
      onClick={onPick}
    >
      <span className={styles.groupHead}>
        {connector && <ConnectorLogo connector={connector} name={name} size={24} radius={6} glyphSize={14} />}
        <Typography variant="h4" className={styles.groupTitle}>
          {name}
        </Typography>
        <Typography variant="p3" color="secondary" className={styles.groupCount}>
          {String(count)}
        </Typography>
      </span>
    </Card>
  );
}
